import { useUser } from "../../context/userContext";
import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDot } from "../../context/automatonContext";
import { useAlert } from "../../context/Feedback/alertContext";
import { Tikz2Dot } from "../Dropzone/Tikz2Dot";
import { getExistingMachineNames, generateUniqueName } from "./utils/Support";
import { saveMachine } from "../../services/saveService";
import DOT from "../../components/MachineElement/DOT";
import Button from "@mui/material/Button";

import importConstants from "../../constants/Dialogs/ImportConstants/importConstants";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const activeStyle = {
  borderColor: "#2196f3",
};

export default function MyDropzone(props) {
  const {
    setDot,
    setCreatedDate,
    setDotName,
    setMachineStates,
    setMachineTransitions,
    machineStates,
    machineTransitions,
    dot,
    automatonType,
  } = useDot();
  const { setAlert } = useAlert();

  const { language, credential, user, setUser } = useUser();
  const [readyToSave, setReadyToSave] = useState(false);

  useEffect(() => {
    if (readyToSave && dot) {
      const save = async () => {
        try {
          const dotString = toDotLanguage();
          await saveMachine({
            dot: dotString,
            dotName: dotNameRef.current,
            createdDate: createdDateRef.current,
            credential,
            user,
            setUser,
            setAlert,
            setCreatedDate,
            language,
            isAutoSave: true,
            automatonType: automatonType,
          });
        } catch (error) {
          setAlert({
            active: true,
            text: "Failed to save machine",
            color: "warning",
            severity: "error",
          });
          console.error("Save error:", error);
        } finally {
          setReadyToSave(false);
        }
      };
      save();
    }
  }, [readyToSave, dot]);

  const dotNameRef = React.useRef(null);
  const createdDateRef = React.useRef(null);

  const processContent = useCallback(
    async (content, fileName) => {
      setDot("");
      setDotName(null);
      setMachineStates([]);
      setMachineTransitions([]);

      const newCreatedDate = new Date().toISOString();
      const existingNames = getExistingMachineNames(user);
      const uniqueFileName = generateUniqueName(fileName, existingNames);
      // Atualiza as refs
      dotNameRef.current = uniqueFileName;
      createdDateRef.current = newCreatedDate;

      setDotName(uniqueFileName);
      setCreatedDate(newCreatedDate);

      if (content.includes("begin")) {
        const novo = Tikz2Dot(content);
        setDot(novo);
      } else {
        setDot(content);
      }
      setReadyToSave(true);
      props.setOpen(false);
    },
    [
      user,
      setDot,
      setDotName,
      setMachineStates,
      setMachineTransitions,
      setCreatedDate,
      setReadyToSave,
      props,
    ]
  );

  const onDropAccepted = useCallback(
    async ([file]) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = async function () {
        const contents = reader.result;
        const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
        processContent(contents, fileNameWithoutExtension);
      };
    },
    [processContent]
  );

  const toDotLanguage = () => {
    if (dot) {
      return dot;
    }

    let dott = new DOT(machineStates, machineTransitions);
    dott.toDotLanguage();
    return dott.toString();
  };

  const onDropRejected = useCallback(() => {
    setAlert({
      active: true,
      text: importConstants.labelAlert.invalidFormat[language],
      color: "warning",
      severity: "error",
    });
  }, [language, setAlert]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      onDropRejected,
      accept: [".dot", ".tikz", ".tex"],
      onDropAccepted,
    });

  useEffect(() => {
    const handlePaste = (event) => {
      if (!props.open) {
        return;
      }
      const pastedText = event.clipboardData.getData("text/plain");
      if (pastedText) {
        event.preventDefault();
        processContent(pastedText, "pasted_from_clipboard");
      }
    };

    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [processContent, props.open]);

  const acceptedFileItems = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
  }));

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        processContent(text, "pasted_from_clipboard");
      }
    } catch (err) {
      console.error("Erro ao colar do clipboard:", err);
      setAlert({
        active: true,
        text: importConstants.labelAlert.invalidFormat[language],
        color: "warning",
        severity: "error",
      });
    }
  };

  return (
    <div>
      <div {...getRootProps({ style })}>
        <input
          {...getInputProps()}
          id="machine-upload-input"
          name="machineUpload"
        />
        <p>{props.text}</p>
        <p>{props.textClipboard}</p>
        <h4>{acceptedFileItems}</h4>
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePasteClick}
          style={{ marginTop: "10px", borderRadius: "8px" }}
        >
          {importConstants.importDialog.paste[language]}
        </Button>
      </div>
    </div>
  );
}
